import React, { useEffect, useState } from "react";
import { Card, Collapse, Button, message, Tooltip } from "antd";
import { useMsal } from "@azure/msal-react";
import { callGetSession } from "components/LaunchPositWorkbenchModal/launchPositWorkbenchApi";
import { LinkCard } from "./types";
import { linksData } from "./constants";
import { ISusData } from "components/SpaceList";
import { getSagemakerDomainByDisplayName } from "utils/utils";
import { InfoCircleOutlined } from "@ant-design/icons";
import {
  GRAFANA_WORKSPACE_BASE_URL,
  GRAFANA_LOG_GROUP_CONFIG,
  BYOC_GRAFANA_DASHBOARD_URL,
} from "const";
import { getApplicationUrl } from "utils/applicationUrl";
import "./style.scss";
import LaunchPositWorkbenchModal from "components/LaunchPositWorkbenchModal";

const BYOC_PROFILE_TYPE = "App Deployment (BYOC)";
 
const LinksSection: React.FC<{ 
  spaceDisplayName: string,
  spaceName?: string,
  sageMakerData: ISusData, 
  domainName: string,
  region?: string,
  bucketName?: string,
  dataAccountId?: string,
  gxpFlag?: string,
  profileType?: string
}> = ({ spaceDisplayName = "Links", spaceName, sageMakerData, domainName, region, bucketName, dataAccountId, gxpFlag, profileType }) => {
  const [showWorkbenchModal, setShowWorkbenchModal] = useState(false);
  const [activeSessionUrl, setActiveSessionUrl] = useState<string | null>(null);
  const msalInstance = useMsal();
  const sagemakerDomain = getSagemakerDomainByDisplayName(domainName) ?? "";

  // When the displayed space changes, check for an active Posit session for that space
  useEffect(() => {
    let cancelled = false;
    const checkSession = async () => {
      setActiveSessionUrl(null);
      if (!spaceDisplayName) return;
      try {
        const result = await callGetSession(msalInstance, spaceDisplayName);
        if (!cancelled && result?.hasActiveSession && result.url) {
          setActiveSessionUrl(result.url);
        }
      } catch (err) {
        if (!cancelled) setActiveSessionUrl(null);
      }
    };

    checkSession();

    return () => {
      cancelled = true;
    };
  }, [spaceDisplayName, msalInstance]);

  // Guard: if linksData is null or undefined, render nothing or a fallback
  if (!linksData || !Array.isArray(linksData) || linksData.length === 0) {
    return <div>No links available.</div>;
  }

  const isByocProfile = profileType === BYOC_PROFILE_TYPE;
  const resolvedSpaceName = spaceName || spaceDisplayName;
  const applicationHost = isByocProfile
    ? getApplicationUrl(resolvedSpaceName, domainName)
    : "";

  // Dynamically update linksData with sagemakerData / BYOC URLs, then apply visibility
  const dynamicLinksData = linksData
    .map(link => {
      if (link.linkFor === "sagemaker_studio" && sageMakerData?.projectLink) {
        return { ...link, url: sageMakerData.projectLink };
      }
      if (link.linkFor === "github_project" && sageMakerData?.gitRepo) {
        return { ...link, url: sageMakerData.gitRepo };
      }
      if (link.linkFor === "business_metadata" && sageMakerData?.susProjectMetadata) {
        return { ...link, url: sageMakerData.susProjectMetadata };
      }
      if (link.linkFor === "grafana_logs" && region && dataAccountId) {
        const base = new URL(`${GRAFANA_WORKSPACE_BASE_URL}/d/lYQ8-oQGf/application-logs`);
        base.searchParams.set("orgId", "1");
        base.searchParams.set("var-Region", region);
        base.searchParams.set("var-Accounts", dataAccountId);
        base.searchParams.set("var-search", "");
        const logGroups = GRAFANA_LOG_GROUP_CONFIG[profileType ?? ""] ?? ["$__all"];
        logGroups.forEach(lg => {
          const logGroupArn = lg === "$__all" 
            ? lg 
            : `arn:aws:logs:${region}:${dataAccountId}:log-group:${lg}:*`;
          base.searchParams.append("var-log_group_name", logGroupArn);
        });
        base.searchParams.set("from", "now-1h");
        base.searchParams.set("to", "now");
        return { ...link, url: base.toString() };
      }
      if (link.linkFor === "application_url" && applicationHost) {
        return { ...link, url: `https://${applicationHost}` };
      }
      if (link.linkFor === "byoc_grafana_logs") {
        const base = new URL(BYOC_GRAFANA_DASHBOARD_URL);
        base.search = "";
        base.searchParams.set("var-Space", resolvedSpaceName ?? "");
        if (region) base.searchParams.set("var-Region", region);
        if (dataAccountId) base.searchParams.set("var-Domain", dataAccountId);
        return { ...link, url: base.toString() };
      }

      return link;
    })
    .filter(link => {
      if (link.linkFor === "application_url") return Boolean(applicationHost);
      if (link.linkFor === "byoc_grafana_logs") return isByocProfile;
      return true;
    });

  const handleButtonClick = (link: LinkCard) => {
    // Special handling for Post Workbench - open modal on click
    if (link.linkFor === "post_workbench") {
      // If an active session exists, open it directly
      if (activeSessionUrl) {
        try {
          window.open(activeSessionUrl, "_blank", "noopener,noreferrer");
        } catch (err) {}
        return;
      }
      setShowWorkbenchModal(true);
      return;
    }
    
    window.open(link.url, "_blank", "noopener,noreferrer");
  };
 
  return (
    <div style={{ backgroundColor: "#f5f5f5" }}>
      <Collapse defaultActiveKey={["1"]} style={{ backgroundColor: "#f5f5f6" }}>
        <Collapse.Panel
          key="1"
          header={
            <div style={{ display: "flex", alignItems: "center" }}>
              <span>
                Links inside <strong>{spaceDisplayName}</strong>
              </span>
            </div>
          }
        >
          <div className="links-cards-grid">
              {dynamicLinksData.map((link) => (
                <Card
                  key={link.title}
                  className="links-card"
                  hoverable
                >
                  <p className="links-card-description">
                    {link.description}
                  </p>
                  
                  <Tooltip
                    title={
                      (!link.url || link.url === "#") && (link.linkFor === "sagemaker_studio" || link.linkFor === "github_project" || link.linkFor === "business_metadata")
                        ? "Link not available. Please refresh the the page or space list"
                        : ""
                    }
                    overlayStyle={{
                      maxWidth: 220,
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                    }}
                  >                    
                    <Button
                      type="primary"
                      onClick={() => handleButtonClick(link)}
                      className="links-card-button"
                      disabled={
                        link.disable || 
                        (link.linkFor !== "post_workbench" && (!link.url || link.url === "#"))
                      }
                      icon={
                        (!link.url || link.url === "#") &&
                          (link.linkFor === "sagemaker_studio" || link.linkFor === "github_project" || link.linkFor === "business_metadata") ? (
                          <InfoCircleOutlined />
                        ) : null
                      }
                    >
                      <span className="links-card-button-label">
                        {link.linkFor === "post_workbench" && activeSessionUrl ? "Open Active Session" : link.buttonText}
                      </span>
                    </Button>
                  </Tooltip>
                </Card>
              ))}
          </div>
        </Collapse.Panel>
      </Collapse>
      {/* Modal for launching Posit Workbench - fetches data on open */}
      <LaunchPositWorkbenchModal
        visible={showWorkbenchModal}
        onCancel={() => setShowWorkbenchModal(false)}
        msalInstance={msalInstance}
        region={region}
        bucketName={bucketName}
        dataAccountId={dataAccountId}
        spaceDisplayName={spaceDisplayName}
        gxpFlag={gxpFlag}
        onLaunched={(url) => {
          setActiveSessionUrl(url);
          setShowWorkbenchModal(false);
        }}
      />
    </div>
  );
};
 
export default LinksSection;